import React, { useState, useEffect } from 'react';
import ActingPlayerView from './components/ActingPlayerView';
import SituationPlayerView from './components/SituationPlayerView';
import { GameState, Player } from '../../../../services/api';
import { useGameActions } from '../../../../hooks/useGameActions';

interface GuessingNumbersProps {
  gameState: GameState;
  playerId: string;
  onStateUpdate: (newState: GameState) => void;
}

const GuessingNumbers: React.FC<GuessingNumbersProps> = ({ gameState, playerId, onStateUpdate }) => {
  const isSituationPlayer = gameState.currentSituationPlayer === playerId;
  
  const [currentPlayerOrder, setCurrentPlayerOrder] = useState<Player[]>([]);

  const { advanceGame, submitGuesses, isLoading } = useGameActions({
    gameCode: gameState.gameCode,
    playerId,
    onStateUpdate
  });

  // Initialize player order when the component mounts or players change
  useEffect(() => {
    if (currentPlayerOrder.length === 0 && gameState.players.length > 1) {
      const numberPlayers = gameState.players.filter(p => p.id !== gameState.currentSituationPlayer);
      const sortedPlayers = [...numberPlayers].sort((a, b) => a.id.localeCompare(b.id));
      setCurrentPlayerOrder(sortedPlayers);
    }
  }, [gameState.players, gameState.currentSituationPlayer, currentPlayerOrder]);

  // Convert ordered list to guesses object
  const getGuessesFromOrder = (orderedPlayers: Player[]) => {
    const guesses: { [playerId: string]: number } = {};
    orderedPlayers.forEach((player, index) => {
      guesses[player.id] = index + 1; // Numbers start from 1
    });
    return guesses;
  };

  // Handle order change from the UpdatablePlayersList component
  const handleOrderChange = (orderedPlayers: Player[]) => {
    setCurrentPlayerOrder(orderedPlayers);
    console.log("Order has changed: " + orderedPlayers.map(p => p.id))
  };

  const handleRevealAnswers = async () => {
    try {
      // Submit current order as guesses and advance to results phase
      const guesses = getGuessesFromOrder(currentPlayerOrder);
      await submitGuesses(guesses);
      await advanceGame(); // This will change game state to 'results' for all players
    } catch (err) {
      console.error('Failed to reveal answers:', err);
    }
  };

  const handleNextRound = async () => {
    try {
      // This would typically navigate to next player selection
      // For now, just advance the game again
      await advanceGame();
    } catch (err) {
      console.error('Failed to proceed to next round:', err);
    }
  };

  // Non-situation player view
  if (!isSituationPlayer) {
    const currentSituationPlayerName = gameState.players.find(
      p => p.id === gameState.currentSituationPlayer
    )?.name;

    return (
      <ActingPlayerView
        gameState={gameState}
        currentPlayerOrder={currentPlayerOrder}
        currentSituationPlayerName={currentSituationPlayerName || 'Unknown Player'}
      />
    );
  }

  // Situation player view (guessing)
  return (
    <SituationPlayerView
      gameState={gameState}
      currentPlayerOrder={currentPlayerOrder}
      onOrderChange={handleOrderChange}
      onRevealAnswers={handleRevealAnswers}
      onNextRound={handleNextRound}
      isLoading={isLoading}
    />
  );
};

export default GuessingNumbers;